"use client"

import { useState } from "react"

import { useTranslations } from "next-intl"

import { NavSheet } from "@/components/marketplace/layout/nav-sheet"
import type { NavCategory } from "@/components/marketplace/types"
import { ArrowUpRightIcon, BackIcon, Button, ChevronIcon, IconButton } from "@/components/ui"
import { LocalizedClientLink } from "@/components/ui/localized-client-link"
import { buildRoute } from "@/lib/routes"

const SHEET_ROW = "font-text text-body text-fg h-12.5 w-full justify-start gap-300 rounded-none px-500 py-0 font-normal"

interface CategorySheetProps {
  categories: NavCategory[]
  onDismiss: () => void
}

export const CategorySheet = ({ categories, onDismiss }: CategorySheetProps) => {
  const t = useTranslations("nav")
  const [openHandle, setOpenHandle] = useState<string>()
  const department = categories.find((entry) => entry.handle === openHandle)
  const children = department?.children ?? []

  if (!department || children.length === 0) {
    return (
      <NavSheet label={t("allCategories")} scrimLabel={t("closeMenu")} onDismiss={onDismiss}>
        <ul className="divide-line-subtle divide-y">
          {categories.map((entry) => {
            const chevron = <ChevronIcon size={22} className="text-fg-brand -mr-200 shrink-0" />
            const expandable = (entry.children?.length ?? 0) > 0
            return (
              <li key={entry.handle}>
                {expandable ? (
                  <Button variant="quiet" fullWidth onClick={() => setOpenHandle(entry.handle)} aria-expanded={false} className={SHEET_ROW}>
                    <span className="min-w-0 flex-1 text-left">{entry.label}</span>
                    {chevron}
                  </Button>
                ) : (
                  <Button variant="quiet" fullWidth asChild onClick={onDismiss} className={SHEET_ROW}>
                    <LocalizedClientLink href={buildRoute.category(entry.path)}>
                      <span className="min-w-0 flex-1 text-left">{entry.label}</span>
                      {chevron}
                    </LocalizedClientLink>
                  </Button>
                )}
              </li>
            )
          })}
        </ul>
      </NavSheet>
    )
  }

  return (
    <NavSheet label={department.label} scrimLabel={t("closeMenu")} onDismiss={onDismiss}>
      <div className="border-line-subtle relative flex h-12.5 items-center border-b px-500">
        <IconButton
          aria-label={t("allCategories")}
          variant="ghost"
          icon={<BackIcon size={22} />}
          onClick={() => setOpenHandle(undefined)}
          className="text-fg-brand absolute left-300 size-8"
        />
        <span className="font-text text-body text-fg min-w-0 flex-1 text-center">{department.label}</span>
      </div>
      <ul className="divide-line-subtle divide-y">
        {children.map((child) => (
          <li key={child.handle}>
            <Button variant="quiet" fullWidth asChild onClick={onDismiss} className={SHEET_ROW}>
              <LocalizedClientLink href={buildRoute.category(child.path)}>
                <span className="min-w-0 flex-1 text-left">{child.label}</span>
              </LocalizedClientLink>
            </Button>
          </li>
        ))}
      </ul>
      <Button
        variant="quiet"
        fullWidth
        asChild
        onClick={onDismiss}
        className={`${SHEET_ROW} border-line-subtle text-fg-brand justify-center gap-200 border-t underline underline-offset-2`}
      >
        <LocalizedClientLink href={buildRoute.category(department.path)}>
          {t("seeAllCategory", { category: department.label })}
          <ArrowUpRightIcon size={19} className="shrink-0" />
        </LocalizedClientLink>
      </Button>
    </NavSheet>
  )
}
